/*
 * Magnetic Solutions – product page extras (volume price table + delivery promise).
 * <ms-product-refresh data-ms-refresh="…">: re-renders its content from the HTML Horizon fetches
 *   when the variant changes ('shopify:product:select' event, same pattern as product-price.js).
 * <ms-volume-tiers>: same, plus highlights the tier that matches the selected quantity
 *   (+ items already in the cart when data-scope="cart", i.e. mix & match automatic discounts).
 */
const SELECT_EVENT = 'shopify:product:select';
const CART_EVENT = 'shopify:cart:lines-update';

class MsProductRefresh extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    if (!this.section) return;
    this.onSelect = (event) => {
      const target = event.target;
      if (target instanceof Element && target.closest('product-card')) return;
      if (!event.promise || typeof event.promise.then !== 'function') return;
      event.promise
        .then((result) => {
          const html = result && result.detail && result.detail.html;
          if (!html || typeof html.querySelector !== 'function') return;
          const fresh = html.querySelector(`[data-ms-refresh="${this.dataset.msRefresh}"]`);
          if (!fresh) {
            // Element not rendered for this variant (e.g. no price): clear it.
            this.innerHTML = '';
            return;
          }
          this.innerHTML = fresh.innerHTML;
          for (const attr of ['data-cart-count', 'data-variant-id', 'data-scope']) {
            if (fresh.hasAttribute(attr)) this.setAttribute(attr, fresh.getAttribute(attr));
          }
          this.afterRefresh();
        })
        .catch(() => {});
    };
    this.section.addEventListener(SELECT_EVENT, this.onSelect);
  }

  disconnectedCallback() {
    if (this.section && this.onSelect) this.section.removeEventListener(SELECT_EVENT, this.onSelect);
  }

  afterRefresh() {}
}

class MsVolumeTiers extends MsProductRefresh {
  connectedCallback() {
    super.connectedCallback();
    this.cartCount = parseInt(this.dataset.cartCount || '0', 10) || 0;
    this.onQty = (event) => {
      const t = event.target;
      if (t instanceof HTMLInputElement && t.name === 'quantity') this.highlight();
      else if (event.type === 'quantity-selector:update') this.highlight();
    };
    this.onCart = (event) => {
      // Horizon dispatches the event before the cart request finishes; wait for its promise.
      const done = event && event.promise && typeof event.promise.then === 'function' ? event.promise : Promise.resolve();
      done
        .catch(() => {})
        .then(() => fetch(`${(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'}cart.js`, {
        headers: { Accept: 'application/json' },
      }))
        .then((r) => r.json())
        .then((cart) => {
          this.cartCount = Number(cart.item_count) || 0;
          this.highlight();
        })
        .catch(() => {});
    };
    const root = this.section || document;
    root.addEventListener('input', this.onQty);
    root.addEventListener('change', this.onQty);
    root.addEventListener('quantity-selector:update', this.onQty);
    document.addEventListener(CART_EVENT, this.onCart);
    this.highlight();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const root = this.section || document;
    root.removeEventListener('input', this.onQty);
    root.removeEventListener('change', this.onQty);
    root.removeEventListener('quantity-selector:update', this.onQty);
    document.removeEventListener(CART_EVENT, this.onCart);
  }

  afterRefresh() {
    this.highlight();
  }

  selectedQuantity() {
    const root = this.section || document;
    const input =
      root.querySelector('product-form-component input[name="quantity"]') ||
      root.querySelector('input[name="quantity"]');
    const q = input ? parseInt(input.value, 10) : 1;
    return Number.isFinite(q) && q > 0 ? q : 1;
  }

  highlight() {
    const rows = this.querySelectorAll('.ms-tiers__row');
    if (!rows.length) return;
    const selected = this.selectedQuantity();
    const mixMatch = this.dataset.scope === 'cart';
    const total = mixMatch ? selected + this.cartCount : selected;
    let active = null;
    rows.forEach((row) => {
      const min = parseInt(row.dataset.min, 10);
      const max = row.dataset.max === '' ? Infinity : parseInt(row.dataset.max, 10);
      const on = total >= min && total <= max;
      row.classList.toggle('is-active', on);
      if (on) active = row;
    });
    const status = this.querySelector('.ms-tiers__status');
    if (!status) return;
    if (mixMatch && this.cartCount > 0 && active) {
      const save = active.querySelector('.ms-tiers__save');
      const pcs = (n) => `${n} ${n === 1 ? 'pc' : 'pcs'}`;
      status.textContent =
        `${pcs(selected)} + ${pcs(this.cartCount)} already in your cart = ${pcs(total)}: ` +
        (save ? `${save.textContent.replace('−', '')} off applies.` : 'list price.');
      status.hidden = false;
    } else {
      status.textContent = '';
      status.hidden = true;
    }
  }
}

if (!customElements.get('ms-product-refresh')) customElements.define('ms-product-refresh', MsProductRefresh);
if (!customElements.get('ms-volume-tiers')) customElements.define('ms-volume-tiers', MsVolumeTiers);
